/**
 * Verifier text often arrives as several clauses (one per sub-check), separated by
 * newlines, ` | `, ` · `, or `; `. Keep each check on its own line for the admin UI.
 */
export function formatGateReasonReadableFlow(raw: string | null | undefined): string {
  if (raw == null || typeof raw !== 'string') return '';
  const lines = raw.split(/\r?\n+|\s+\|\s+|\s+·\s+|;\s+/);
  const clauses: string[] = [];
  for (const line of lines) {
    const t = line.replace(/\s+/g, ' ').trim();
    if (t) clauses.push(t);
  }
  if (clauses.length === 0) return '';
  if (clauses.length === 1) return clauses[0]!;
  return clauses.join('\n');
}

export type ParsedFailReasonSignal = {
  label: string;
  status: 'pass' | 'fail' | 'review';
  detail: string;
};

/** Parses newline-separated gate sub-check lines like `Registry Status — FAIL: NOT_FOUND`. */
export function parseFailReasonSignalLines(full: string | null | undefined): ParsedFailReasonSignal[] {
  const t = full?.trim();
  if (!t) return [];
  const re = /^(.+?)\s*[—–-]\s*(PASS|FAIL|REVIEW)\s*:\s*(.*)$/i;
  const out: ParsedFailReasonSignal[] = [];
  for (const line of t.split(/\r?\n+/)) {
    const trimmed = line.replace(/\s+/g, ' ').trim();
    if (!trimmed) continue;
    const m = trimmed.match(re);
    if (!m) continue;
    const statusU = m[2].trim().toUpperCase();
    const status: ParsedFailReasonSignal['status'] =
      statusU === 'PASS' ? 'pass' : statusU === 'FAIL' ? 'fail' : 'review';
    out.push({ label: m[1].trim(), status, detail: m[3].trim() });
  }
  return out;
}

type HeadlineRule = { test: (s: string) => boolean; headline: string };

type SummaryGateFamily = 'EXISTENCE' | 'LINKAGE' | 'LEGITIMACY' | 'STRUCTURAL_SANITY' | 'TEMPORAL';

const SUMMARY_GATE_DEFAULTS: Record<SummaryGateFamily, string> = {
  EXISTENCE: 'Employer could not be confirmed in registry',
  LINKAGE: 'Applicant–employer link could not be verified',
  LEGITIMACY: 'Employer legitimacy checks did not pass',
  STRUCTURAL_SANITY: 'Role, sector, or workplace checks did not pass',
  TEMPORAL: 'Employment dates or tenure did not pass checks',
};

/** Align keyword rules with employer-verification gate-reasons.constants.ts phrasing. */
const LINKAGE_HEADLINE_RULES: HeadlineRule[] = [
  {
    test: (s) => /personal\s+e-transfer|personal\s+name\s+transfer/i.test(s),
    headline: 'Payment appears to be a personal transfer, not payroll',
  },
  {
    test: (s) => /bank transaction string does not match/i.test(s),
    headline: 'Bank payments do not match the declared employer',
  },
  {
    test: (s) => /not a recognized payroll provider/i.test(s),
    headline: 'Payor is not a recognized payroll provider',
  },
  {
    test: (s) => /not on file as a director/i.test(s),
    headline: 'Applicant–employer link could not be confirmed',
  },
  {
    test: (s) => /no bank transaction string was provided/i.test(s),
    headline: 'No bank transaction data to establish linkage',
  },
  {
    test: (s) => /unrecognized/i.test(s) && /manual review/i.test(s),
    headline: 'Payor could not be matched to the employer',
  },
  {
    test: (s) => /linkage inconclusive|linkage check bypassed/i.test(s),
    headline: 'Applicant–employer link is inconclusive',
  },
];

const LEGITIMACY_HEADLINE_RULES: HeadlineRule[] = [
  {
    test: (s) => /fewer reviews than the minimum/i.test(s),
    headline: 'Employer has insufficient digital footprint',
  },
  {
    test: (s) => /google business status/i.test(s) || /not operational/i.test(s),
    headline: 'Employer does not appear operational',
  },
  {
    test: (s) => /no active directors/i.test(s),
    headline: 'No active directors on file for the employer',
  },
  {
    test: (s) => /whois status\s+expired/i.test(s),
    headline: 'Employer website domain is expired',
  },
  {
    test: (s) => /had no website to check/i.test(s),
    headline: 'AI digital footprint had no website to check',
  },
  {
    test: (s) => /shifted to manual review/i.test(s),
    headline: 'Employer legitimacy requires manual review',
  },
];

const STRUCTURAL_HEADLINE_RULES: HeadlineRule[] = [
  {
    test: (s) => /sector-infrastructure contradiction/i.test(s),
    headline: 'Declared industry sector does not match verified workplace',
  },
  {
    test: (s) => /working type conflict/i.test(s),
    headline: 'Declared working type is inconsistent with the role',
  },
  {
    test: (s) => /sector-role mismatch/i.test(s),
    headline: 'Job title does not match the declared industry sector',
  },
  {
    test: (s) => /infrastructure mismatch/i.test(s),
    headline: 'Workplace type does not match the declared role',
  },
  {
    test: (s) => /commute distance/i.test(s) && /exceeds/i.test(s),
    headline: 'Commute distance exceeds policy limits',
  },
  {
    test: (s) => /inter-provincial daily commute/i.test(s),
    headline: 'Inter-provincial commute is unrealistic for this role',
  },
  {
    test: (s) => /shifted to manual review/i.test(s),
    headline: 'Role and workplace checks require manual review',
  },
];

const TEMPORAL_HEADLINE_RULES: HeadlineRule[] = [
  {
    test: (s) => /mutation detected/i.test(s),
    headline: 'Application data changed since the last verification',
  },
  {
    test: (s) => /predates the company incorporation/i.test(s),
    headline: 'Employment start date predates company incorporation',
  },
  {
    test: (s) => /work authorization permit has expired/i.test(s),
    headline: 'Work authorization appears expired',
  },
  {
    test: (s) => /more than/i.test(s) && /days in the future/i.test(s),
    headline: 'Employment start date is too far in the future',
  },
  {
    test: (s) => /employment eligibility failure/i.test(s),
    headline: 'Employment eligibility did not pass checks',
  },
];

function normalizeForHeadlineCompare(s: string): string {
  return s.replace(/\s+/g, ' ').trim().toLowerCase();
}

function reasonSummaryOneLineForCompare(full: string, maxLen = 220): string {
  const t = full.trim();
  if (!t) return '';
  const idx = t.indexOf('. ');
  if (idx > 0 && idx + 1 <= maxLen) return t.slice(0, idx + 1);
  if (t.length <= maxLen) return t;
  return `${t.slice(0, Math.max(0, maxLen - 1))}…`;
}

function isRegistryNotFoundText(u: string): boolean {
  return (
    u.includes('NOT_FOUND') ||
    u.includes('NOT FOUND') ||
    (u.includes('CORPORATE REGISTRY') && u.includes('NOT_FOUND')) ||
    (u.includes('REGISTRY') && u.includes('DISSOLVED')) ||
    (u.includes('STRUCK') && u.includes('OFF'))
  );
}

function matchHeadlineRules(rules: HeadlineRule[], text: string): string | null {
  for (const rule of rules) {
    if (rule.test(text)) return rule.headline;
  }
  return null;
}

function headlineFromExistenceCheckpoint(label: string, detail: string): string | null {
  const l = label.toLowerCase();
  const d = detail.toLowerCase();
  const combinedU = `${label} ${detail}`.toUpperCase();

  if (l.includes('employer lookup') || d.includes('not found in official corporate registry')) {
    return 'Employer was not found in the corporate registry';
  }
  if (l.includes('registry status')) {
    if (isRegistryNotFoundText(combinedU)) {
      return 'Employer registry could not be verified';
    }
    return 'Corporate registry status did not confirm an active employer';
  }
  if (l.includes('address') || d.includes('address could not be verified')) {
    return 'Employer address could not be verified';
  }
  if (l.includes('phone') || d.includes('phone number is non-functional')) {
    return 'Employer phone number could not be verified';
  }
  if (d.includes('shifted to manual review')) {
    return 'Employer existence requires manual review';
  }
  return null;
}

function stripWrappedGateDetail(text: string): { gate: SummaryGateFamily; detail: string } {
  const t = text.trim();
  if (/^Structural\s+Sanity\s+Failure:/i.test(t)) {
    return {
      gate: 'STRUCTURAL_SANITY',
      detail: t.replace(/^Structural\s+Sanity\s+Failure:\s*/i, '').trim(),
    };
  }
  if (/^Structural\s+checks\s+are\s+partially\s+inconclusive:/i.test(t)) {
    return {
      gate: 'STRUCTURAL_SANITY',
      detail: t.replace(/^Structural\s+checks\s+are\s+partially\s+inconclusive:\s*/i, '').trim(),
    };
  }
  if (/^Legitimacy\s+concerns\s+found:/i.test(t)) {
    return {
      gate: 'LEGITIMACY',
      detail: t.replace(/^Legitimacy\s+concerns\s+found:\s*/i, '').trim(),
    };
  }
  if (/^Legitimacy\s+data\s+incomplete:/i.test(t)) {
    return {
      gate: 'LEGITIMACY',
      detail: t.replace(/^Legitimacy\s+data\s+incomplete:\s*/i, '').trim(),
    };
  }
  if (/^Temporal\s+Impossibility:/i.test(t)) {
    return {
      gate: 'TEMPORAL',
      detail: t.replace(/^Temporal\s+Impossibility:\s*/i, '').trim(),
    };
  }
  if (/^Temporal\s+data\s+incomplete:/i.test(t)) {
    return {
      gate: 'TEMPORAL',
      detail: t.replace(/^Temporal\s+data\s+incomplete:\s*/i, '').trim(),
    };
  }
  return { gate: 'LINKAGE', detail: t };
}

function headlineForWrappedGate(gate: SummaryGateFamily, detail: string, fullText: string): string {
  const hay = `${detail} ${fullText}`;
  switch (gate) {
    case 'STRUCTURAL_SANITY':
      return matchHeadlineRules(STRUCTURAL_HEADLINE_RULES, hay) ?? SUMMARY_GATE_DEFAULTS.STRUCTURAL_SANITY;
    case 'LEGITIMACY':
      return matchHeadlineRules(LEGITIMACY_HEADLINE_RULES, hay) ?? SUMMARY_GATE_DEFAULTS.LEGITIMACY;
    case 'TEMPORAL':
      return matchHeadlineRules(TEMPORAL_HEADLINE_RULES, hay) ?? SUMMARY_GATE_DEFAULTS.TEMPORAL;
    default:
      return matchHeadlineRules(LINKAGE_HEADLINE_RULES, fullText) ?? SUMMARY_GATE_DEFAULTS.LINKAGE;
  }
}

function applySummaryHeadlineDedup(
  headline: string,
  reasonFocus: string,
  gate: SummaryGateFamily,
): string {
  const body = reasonSummaryOneLineForCompare(reasonFocus);
  if (normalizeForHeadlineCompare(headline) === normalizeForHeadlineCompare(body)) {
    return SUMMARY_GATE_DEFAULTS[gate];
  }
  return headline;
}

/**
 * Short operator headline for the employer verification summary card (under legal name).
 * Full technical text stays in the Failure reason block.
 */
export function summaryHeadlineFromFailReason(params: {
  reasonFocus: string | null;
  reasonRaw?: string | null;
}): string | null {
  const focus = params.reasonFocus?.trim() ?? '';
  const raw = params.reasonRaw?.trim() ?? '';
  if (!focus && !raw) return null;

  const parsed = parseFailReasonSignalLines(raw || focus);
  if (parsed.length > 0) {
    const attention = parsed.find((s) => s.status === 'fail') ?? parsed.find((s) => s.status === 'review');
    const gate: SummaryGateFamily = 'EXISTENCE';
    if (attention) {
      const headline =
        headlineFromExistenceCheckpoint(attention.label, attention.detail) ?? SUMMARY_GATE_DEFAULTS.EXISTENCE;
      return applySummaryHeadlineDedup(headline, focus || raw, gate);
    }
    return applySummaryHeadlineDedup(SUMMARY_GATE_DEFAULTS.EXISTENCE, focus || raw, gate);
  }

  const text = focus || raw;
  const { gate, detail } = stripWrappedGateDetail(text);
  let headline: string;
  if (gate === 'LINKAGE') {
    headline = matchHeadlineRules(LINKAGE_HEADLINE_RULES, text) ?? SUMMARY_GATE_DEFAULTS.LINKAGE;
  } else {
    headline = headlineForWrappedGate(gate, detail, text);
  }
  return applySummaryHeadlineDedup(headline, focus || raw, gate);
}

/** Operator-facing cause headline from a fail/review clause (registry-aware). */
export function investigationCauseFromFailClause(clause: string): string {
  const raw = clause.trim();
  if (!raw) return '';
  const u = raw.toUpperCase();
  if (
    u.includes('NOT_FOUND') ||
    u.includes('NOT FOUND') ||
    (u.includes('CORPORATE REGISTRY') && u.includes('NOT_FOUND')) ||
    (u.includes('REGISTRY') && u.includes('DISSOLVED')) ||
    (u.includes('STRUCK') && u.includes('OFF'))
  ) {
    return 'Registry verification did not find an active employer record.';
  }
  const stripped = raw
    .replace(/^[^:]+:\s*(PASS|FAIL|REVIEW)\s*:\s*/i, '')
    .replace(/^[\s—–-]+/, '')
    .trim();
  const basis = stripped || raw;
  const dot = basis.indexOf('. ');
  if (dot > 0 && dot < 220) return basis.slice(0, dot + 1).trim();
  if (basis.length <= 220) return basis.endsWith('.') ? basis : `${basis}.`;
  return `${basis.slice(0, 219).trim()}…`;
}
